import {
  Button,
  CONCEPT_ICONS,
  Icons,
  PocketRow,
  Spinner,
} from "@/components/ui";
import { BROWSERS, type BrowserId } from "@/desktop/browser-connection";
import { useT } from "@/i18n";
import { settingsBrowserMessages } from "@/i18n/messages/settings-browser";
import { ConnectionMessage, ConnectionStatus } from "./browser-connection-chip";
import { useAppleEventsConnection } from "./use-apple-events-connection";

/**
 * The row of a browser only Apple Events connect (Safari): its status with a
 * dot - when it was last verified in the tooltip - and its actions. Connected:
 * Reconnect and Disconnect as icon buttons. Not connected: one short button
 * (Connect, Guide me, Restart Dia and connect). Working: a spinner and Stop.
 */
export function BrowserConnectRow({ browserId }: { browserId: BrowserId }) {
  const t = useT(settingsBrowserMessages);
  const apple = useAppleEventsConnection(browserId);
  if (!apple.applies) return null;
  const { phase } = apple;
  const disabled = phase === "unavailable";
  return (
    <PocketRow
      testId={`connections-${browserId}`}
      title={BROWSERS[browserId].name}
      leading={<CONCEPT_ICONS.app className="h-4 w-4 text-muted" />}
      secondLine={
        <ConnectionStatus
          testId={`browser-connect-status-${browserId}`}
          tone={
            apple.connected
              ? "connected"
              : phase === "needs_you"
                ? "attention"
                : "idle"
          }
          label={apple.label}
          tooltip={apple.verified}
        />
      }
      trailing={
        phase === "working" ? (
          <>
            <Spinner
              className="h-3.5 w-3.5 text-muted"
              data-testid={`browser-connect-working-${browserId}`}
            />
            {apple.stoppable ? (
              <Button
                size="sm"
                variant="ghost"
                data-testid={`browser-connect-stop-${browserId}`}
                onClick={() => apple.run("stop")}
              >
                {t("stop")}
              </Button>
            ) : null}
          </>
        ) : phase === "connected" || (disabled && apple.hasAccess) ? (
          <>
            <Button
              size="iconSm"
              variant="ghost"
              aria-label={t("reconnect")}
              title={t("reconnect")}
              disabled={disabled}
              data-testid={`browser-connect-reconnect-${browserId}`}
              onClick={() => apple.run("check")}
            >
              <Icons.RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
            <Button
              size="iconSm"
              variant="ghost"
              aria-label={t("disconnect")}
              title={t("disconnect")}
              disabled={disabled}
              data-testid={`browser-connect-disconnect-${browserId}`}
              onClick={() => apple.run("disconnect")}
            >
              <Icons.Unplug className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            disabled={disabled}
            title={apple.unavailable ?? undefined}
            data-testid={`browser-connect-${apple.connect.action === "guide" ? "guide-" : ""}${browserId}`}
            onClick={apple.activate}
          >
            {apple.connect.label}
          </Button>
        )
      }
      below={
        apple.message ? (
          <ConnectionMessage message={apple.message} />
        ) : undefined
      }
    />
  );
}
